import React, { useState, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PDFPreview from './PDFPreview';
import { config } from '../config/config';
import SkinTips from './SkinTips';

const PDFGenerator = ({ 
  nextStep, 
  previousStep, 
  appState, 
  setError,
  setLoading 
}) => {
  const [pdfState, setPdfState] = useState({
    isGenerating: false,
    isGenerated: false,
    pdfBlob: null,
    error: null,
    generatedAt: null
  });

  const pdfPreviewRef = useRef(null);

  // Check for existing PDF when component mounts
  useEffect(() => {
    if (appState.analysisResult?.pdfBlob) {
      // PDF already generated, just set state
      setPdfState(prev => ({
        ...prev,
        isGenerated: true,
        pdfBlob: appState.analysisResult.pdfBlob
      }));
    }
  }, []);

  // Enhanced PDF generation using the React component directly
  const generateEnhancedPDF = async () => {
    if (!appState.analysisResult || !appState.patientData) {
      // HIPAA Compliance: Removed console logging
setError('Missing required data for PDF generation. Please complete previous steps.');
      return;
    }

    // HIPAA Compliance: Removed console logging
setPdfState(prev => ({ 
      ...prev, 
      isGenerating: true, 
      error: null 
    }));
    
    try {
      setLoading(true);
      
      // Wait a moment for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Enhanced rendering with retry capability and font loading
      const canvas = await html2canvas(pdfPreviewRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f3f4f6',
        imageTimeout: 0,
        removeContainer: true,
        foreignObjectRendering: false,
        onclone: (clonedDoc) => {
          // Ensure fonts are loaded in the cloned document
          const clonedElement = clonedDoc.body;
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
          }
        },
        width: pdfPreviewRef.current?.scrollWidth || 816,
        height: pdfPreviewRef.current?.scrollHeight || 2246,
        scrollX: 0,
        scrollY: 0,
        windowWidth: pdfPreviewRef.current?.scrollWidth || 816,
        windowHeight: pdfPreviewRef.current?.scrollHeight || 2246,
        ignoreElements: (element) => {
          return element.classList?.contains('sr-only');
        },
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });
      
      // Convert to PNG for maximum quality
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Slice the big canvas into two real pages using DOM anchors
      const host = pdfPreviewRef.current;
      const rectHost = host.getBoundingClientRect();
      const page1El = host.querySelector('#pdf-page-1');
      const page2El = host.querySelector('#pdf-page-2');
      const scaleFactor = canvas.height / rectHost.height; // canvas px per CSS px

      const mkSlice = (el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const topCss = r.top - rectHost.top;
        const heightCss = r.height;
        return {
          sy: Math.max(0, Math.round(topCss * scaleFactor)),
          sh: Math.max(1, Math.round(heightCss * scaleFactor))
        };
      };

      const s1 = mkSlice(page1El);
      const s2 = mkSlice(page2El);

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      const ctx = pageCanvas.getContext('2d');

      const addSlice = (slice, isFirst) => {
        if (!slice || slice.sh < 5) return;
        pageCanvas.height = slice.sh;
        ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0, slice.sy, canvas.width, slice.sh,
          0, 0, pageCanvas.width, pageCanvas.height
        );
        const data = pageCanvas.toDataURL('image/png', 1.0);
        if (!isFirst) pdf.addPage();
        const mmHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        pdf.addImage(data, 'PNG', 0, 0, imgWidth, Math.min(pageHeight, mmHeight), undefined, 'SLOW');
      };

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: false,
        precision: 16
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      addSlice(s1, true);
      addSlice(s2, false);

      // HIPAA Compliance: Removed console logging
// Create optimized PDF blob
      const pdfBlob = pdf.output('blob');
      // HIPAA Compliance: Removed console logging
setPdfState(prev => ({
        ...prev,
        isGenerating: false,
        isGenerated: true,
        pdfBlob: pdfBlob,
        generatedAt: new Date().toISOString()
      }));

    } catch (error) {
      // HIPAA Compliance: Removed console logging
// HIPAA Compliance: Removed console logging
const errorMessage = error.message || 'PDF generation failed. Please try again.';
      
      setPdfState(prev => ({
        ...prev,
        isGenerating: false,
        error: errorMessage
      }));
      
      setError(`PDF Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!pdfState.pdfBlob) {
      setError('No PDF available for download. Please generate the report first.');
      return;
    }

    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const filename = `MySkinAI-Dermatology-Report-${appState.patientData.name.replace(/\s+/g, '-')}-${timestamp}.pdf`;
      
      const url = URL.createObjectURL(pdfState.pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show download success notification
      if ('Notification' in window) {
        if (Notification.permission === 'default') {
          await Notification.requestPermission();
        }
        if (Notification.permission === 'granted') {
          new Notification('📥 Report Downloaded Successfully!', {
            body: `Your skin analysis report has been saved as ${filename}`,
            icon: '/favicon.ico'
          });
        }
      }

      // Visual feedback
      const successMessage = document.createElement('div');
      successMessage.innerHTML = `
        <div style="
          position: fixed; 
          top: 20px; 
          right: 20px; 
          background: linear-gradient(135deg, #10B981, #059669); 
          color: white; 
          padding: 16px 24px; 
          border-radius: 12px; 
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.25); 
          z-index: 10000;
          font-family: system-ui, -apple-system, sans-serif;
          font-weight: 600;
          font-size: 14px;
          animation: slideIn 0.3s ease-out;
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 18px;">📥</span>
            <div>
              <div style="font-weight: 700;">Report Downloaded!</div>
              <div style="font-size: 12px; opacity: 0.9; margin-top: 2px;">File saved: ${filename}</div>
            </div>
          </div>
        </div>
        <style>
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOut {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
        </style>
      `;
      
      document.body.appendChild(successMessage.firstElementChild);

      // Remove message after 5 seconds
      setTimeout(() => {
        const messageEl = document.body.lastElementChild;
        if (messageEl && messageEl.style.position === 'fixed') {
          messageEl.style.animation = 'slideOut 0.3s ease-out forwards';
          setTimeout(() => {
            try {
              document.body.removeChild(messageEl);
            } catch (e) {
              // HIPAA Compliance: Removed console logging
}
          }, 300);
        }
      }, 5000);

      // HIPAA Compliance: Removed console logging
} catch (error) {
      // HIPAA Compliance: Removed console logging
setError('Failed to download PDF. Please try again.');
    }
  };

  const retryGeneration = () => {
    setPdfState({
      isGenerating: false,
      isGenerated: false,
      pdfBlob: null,
      error: null,
      generatedAt: null
    });
    generateEnhancedPDF();
  };

  const handleContinue = () => {
    nextStep();
  };

  // Loading state - Mobile Optimized
  if (pdfState.isGenerating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 text-center max-w-md w-full animate-fade-in-up">
          <div className="w-20 h-20 mx-auto mb-8 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"></div>
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Generating Your Report
          </h2>
          <p className="text-gray-600 mb-6">
            Creating a professional PDF with your analysis results and my-skin.ai branding
          </p>

          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-green-800 text-sm font-medium mb-1">🔒 Client-Side Processing</p>
                <p className="text-green-700 text-xs leading-relaxed">
                  Your PDF is being generated locally on your device. No data is sent to our servers during this process.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce mr-2"></div>
              <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce mr-2" style={{animationDelay: '0.1s'}}></div>
              <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
            <p className="text-blue-800 text-sm">Processing your skin analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - Mobile Optimized
  if (pdfState.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 text-center max-w-md w-full animate-fade-in-up">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-8">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            PDF Generation Failed
          </h2>
          <p className="text-gray-600 mb-6">
            We encountered an error while generating your report
          </p>

          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <p className="text-red-800 text-sm mb-6">{pdfState.error}</p>
            
            <div className="space-y-3">
              <button 
                onClick={retryGeneration} 
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
              >
                Try Again
              </button>
              <button 
                onClick={previousStep} 
                className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all duration-200"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main state - Show PDF options or skip to doctors
  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-50 via-skin-50 to-accent-pink/5">
      {/* Hidden PDF Preview for generation */}
      <div className="sr-only">
        <PDFPreview
          ref={pdfPreviewRef}
          analysisData={appState.analysisResult}
          patientData={appState.patientData}
          capturedImage={appState.capturedImage}
        />
      </div>

      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left Side - Report Content */}
        <div className="flex items-center justify-center p-6">
          <div className="w-full max-w-md">
          {/* Patient Information */}
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Patient Information
            </h3>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-600">Name:</span> <span className="font-medium">{appState.patientData.name}</span></div>
              <div><span className="text-gray-600">Age:</span> <span className="font-medium">{appState.patientData.age} years</span></div>
              <div><span className="text-gray-600">Gender:</span> <span className="font-medium capitalize">{appState.patientData.gender}</span></div>
            </div>
          </div>

          {/* Report Details */}
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center text-lg">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Report Details
            </h3>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-600">Generated:</span> <span className="font-medium">{new Date().toLocaleDateString()}</span></div>
              <div><span className="text-gray-600">Format:</span> <span className="font-medium">Professional PDF</span></div>
              <div><span className="text-gray-600">Status:</span> <span className="font-medium text-green-600">Ready for Download</span></div>
            </div>
          </div>

          {/* Action Buttons - Mobile Optimized */}
          <div className="space-y-3">
            {/* Primary Download Button */}
            <button
              onClick={downloadPDF}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center text-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Report
            </button>

            {/* Skip to Doctors Button */}
            <button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center text-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
              </svg>
              Find Nearby Dermatologists
            </button>
            
            {/* Optional PDF Generation */}
            {!pdfState.isGenerated && (
              <button
                onClick={generateEnhancedPDF}
                disabled={pdfState.isGenerating}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center text-sm"
              >
                {pdfState.isGenerating ? (
                  <>
                    <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Generate PDF Report (Optional)
                  </>
                )}
              </button>
            )}
            
            {/* Back Button */}
            <div className="text-center pt-2">
              <button
                onClick={previousStep}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm underline transition-colors duration-200"
              >
                ← Back to Analysis
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Skin Tips */}
      <div className="bg-gray-50 p-6 flex flex-col justify-center">
        <SkinTips />
      </div>
    </div>

    {/* Mobile Layout - Stack vertically */}
    <style jsx>{`
      @media (max-width: 1024px) {
        .grid {
          display: block !important;
        }
      }
    `}</style>
  </div>
  );
};

export default PDFGenerator;
